import { memo, useContext } from 'react';

import PersonInPlaceEditor from '@/components/PersonInPlaceEditor';
import { FieldTypes } from '@/components/PersonInPlaceEditor/types';
import type { ArrayFieldValue } from '@/components/PersonInPlaceEditor/types';
import { phoneTypes } from '@/constants/memberContactInfo';
import useStartCall from '@/hooks/useStartCall';
import PersonContext from '@/pages/Person/PersonContext';
import { phoneNumbersSchema } from '@/pages/Profile/schemas';
import type { Person } from '@/types/person';
import DataList, { DataRow } from '@/ui/components/DataList';
import Heading from '@/ui/components/Heading';

import PhoneNumber from './PhoneNumber';

const PhoneNumberSection = () => {
  const { person } = useContext(PersonContext);
  const handleCall = useStartCall(person.id);

  const hasPhones = !!person.phones.length;

  return (
    <div className="group/subsection mb-4 relative">
      <Heading className={`${hasPhones ? 'mb-2' : ''}`} value={4} text="Phone Number" />
      <PersonInPlaceEditor
        subsectionEditor
        schema={phoneNumbersSchema}
        fields={{
          key: 'phones',
          newObject: (currentPhones: { _delete?: boolean }[]) =>
            ({
              id: undefined,
              default: currentPhones.every((phone) => phone._delete),
              kind: 'mobile',
              number: '+'
            }) as ArrayFieldValue<Person, 'phones'>,
          deleteConfirmMessage:
            "This is the member's only phone number. Deleting it will disable texting and calling for this member. Continue?",
          fields: [
            {
              key: (idx: number) => `phones.${idx}.id`,
              label: '',
              type: FieldTypes.HIDDEN
            },
            {
              key: (idx: number) => `phones.${idx}.default`,
              label: 'Default',
              type: FieldTypes.BOOLEAN
            },
            {
              key: (idx: number) => `phones.${idx}.number`,
              label: 'Phone Number',
              type: FieldTypes.TEXT
            },
            {
              key: (idx: number) => `phones.${idx}.kind`,
              label: 'Kind',
              type: FieldTypes.RADIO,
              options: phoneTypes.map((type) => ({ label: type, value: type }))
            }
          ]
        }}>
        <DataList>
          {hasPhones ? (
            <>
              {person.phones.map((phone, idx) => (
                <DataRow
                  key={`member-phone-${phone.id}`}
                  label={`#${idx + 1} (${phone.default ? 'Default | ' : ''}${phone.kind})`}>
                  <PhoneNumber phone={phone} onCall={handleCall} />
                </DataRow>
              ))}
            </>
          ) : (
            <DataRow label="None" />
          )}
        </DataList>
      </PersonInPlaceEditor>
    </div>
  );
};

export default memo(PhoneNumberSection);
